// ⬡B:msria-home.chat:SURFACE:conversation_glass_on_msria_channel:20260604⬡
// ⬡B:msria-home.chat.ms_mac_import:CODE:ms_mac_persona_clone_fix:20260607⬡
// Smoke, Mark, and C tokens now imported from @aba/ms-mac-core.
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useHam } from '@aba/ham-core';
import { createAbaClient } from '@aba/api-core';
import { useNavigate } from 'react-router-dom';
import { C, Smoke, Mark } from '@aba/ms-mac-core';

function Bubble({ message }) {
  const ria = ['aba', 'assistant', 'ria', 'mac'].includes(message.role);
  return (
    <div style={{ display: 'flex', justifyContent: ria ? 'flex-start' : 'flex-end', margin: '0 0 10px', padding: '0 14px', alignItems: 'flex-end', gap: 9 }}>
      {ria && (
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#e8b4a0,#b08d57)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 0 10px rgba(232,150,170,.4)', fontWeight: 600, fontSize: 14, color: '#0a0a0c' }}>R</div>
      )}
      <div style={{
        maxWidth: '78%', padding: '12px 16px',
        borderRadius: ria ? '18px 18px 18px 5px' : '18px 18px 5px 18px',
        background: ria ? 'rgba(255,255,255,.06)' : 'linear-gradient(135deg,rgba(232,150,170,.16),rgba(176,141,87,.14))',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid ' + (ria ? C.hair : 'rgba(232,150,170,.3)'),
        color: ria ? C.text : C.platinum, fontSize: 15, lineHeight: 1.62, whiteSpace: 'pre-wrap',
        boxShadow: '0 2px 16px rgba(0,0,0,.4)', fontWeight: ria ? 400 : 500,
      }}>
        {message.text}
      </div>
    </div>
  );
}

function Dots() {
  return (
    <div style={{ display: 'flex', gap: 5, padding: '4px 18px 12px', alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: C.rose, opacity: .8, animation: `riadot 1s ${i * 0.18}s infinite ease-in-out` }} />
      ))}
      <style>{'@keyframes riadot{0%,100%{transform:translateY(0);opacity:.4}50%{transform:translateY(-4px);opacity:1}}'}</style>
    </div>
  );
}

export default function Chat() {
  const { ham, supabaseClient, signOut, loading } = useHam();
  const nav = useNavigate();
  const client = useMemo(
    () => (ham?.acl_uid ? createAbaClient({ hamHint: { aclUid: ham.acl_uid }, supabaseClient }) : null),
    [ham?.acl_uid, supabaseClient],
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  // Guard: wait for auth to fully settle before redirecting to /meet.
  // INITIAL_SESSION fires (ham=null, loading=false) before SIGNED_IN resolves.
  // A 2s debounce ensures auth has time to complete before we declare "not logged in".
  useEffect(() => {
    if (!loading && !ham) {
      const t = setTimeout(() => nav('/meet'), 2000);
      return () => clearTimeout(t);
    }
  }, [loading, ham, nav]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || !client || sending) return;
    setMessages(m => [...m, { role: 'user', text }]);
    setInput('');
    setSending(true);
    try {
      const r = await client.air({ message: text, channel: 'msria' });
      const reply =
        (r && (r.text || r.response || r.reply || r.message)) ||
        (typeof r === 'string' ? r : '') ||
        'I lost my footing for a second there. Say that again for me?';
      setMessages(m => [...m, { role: 'ria', text: reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'ria', text: 'I lost the connection for a moment. One more time?' }]);
    } finally {
      setSending(false);
    }
  }, [input, client, sending]);

  return (
    <div style={{ position: 'relative', width: '100%', minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: C.ink, color: C.text, fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Inter,system-ui,sans-serif' }}>
      <Smoke />
      <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.hair}` }}>
        <Mark size={20} />
        <button onClick={() => signOut && signOut()} style={{ background: 'transparent', border: `1px solid ${C.hair}`, color: C.muted, borderRadius: 10, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>Sign out</button>
      </div>

      <div style={{ position: 'relative', zIndex: 2, flex: 1, overflowY: 'auto', padding: '22px 0 8px' }}>
        {messages.length === 0 && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 32px' }}>
            <Mark size={34} />
            <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.7, maxWidth: 360, marginTop: 24 }}>
              I'm here. Tell me what's on your plate, the big thing or the small one, and we'll take it from there.
            </p>
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} message={m} />)}
        {sending && <Dots />}
        <div ref={endRef} />
      </div>

      <div style={{ position: 'relative', zIndex: 2, display: 'flex', gap: 10, padding: '14px 16px calc(20px + env(safe-area-inset-bottom))', borderTop: `1px solid ${C.hair}` }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Talk to Ms.Ria"
          style={{ flex: 1, background: 'rgba(255,255,255,.06)', border: `1px solid ${C.hair}`, borderRadius: 14, color: C.text, padding: '13px 16px', fontSize: 15, outline: 'none' }}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sending}
          style={{ background: input.trim() && !sending ? 'linear-gradient(135deg,#e8b4a0,#b08d57)' : 'rgba(255,255,255,.08)', border: 'none', borderRadius: 14, color: input.trim() && !sending ? '#0a0a0c' : C.muted, padding: '0 22px', fontSize: 15, fontWeight: 700, cursor: input.trim() && !sending ? 'pointer' : 'default' }}
        >Send</button>
      </div>
    </div>
  );
}